import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import toast from 'react-hot-toast'
import { twMerge } from 'tailwind-merge'
import useUser from 'shared/hooks/use-user'
import PinIcon from 'shared/icons/pin-icon'
import { communityPostKeys } from 'modules/community/api/key-factory'
import { useUnpinPost } from 'modules/community/api/use-unpin-post'
import { mutatePostsByPin } from 'modules/community/components/pinned-post/utils'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import type { PostInterface } from 'modules/community/types/post-interface'
import type { PostPinnedToType } from 'modules/community/types/post-pinned-to-type'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'
import { isPinnedIn } from 'modules/community/utils/pinned-post'

interface PostPinnedHeaderProps {
  post: PostInterface
  className?: string
}

export const PostPinnedHeader = ({ post, className }: PostPinnedHeaderProps): ReactNode => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { query } = useRouter()
  const { communityPath, topicPath } = query
  const { user } = useUser()
  const { member } = useIsUserMember()
  const unpinMutation = useUnpinPost({ postId: post.id })
  const [isPostUnpinnable] = hasPermissions(user, 'posts', ['unpin'], { post, member })

  const handleUnpin = async (post: PostInterface, deleteIn: PostPinnedToType): Promise<void> => {
    try {
      await unpinMutation.trigger({ data: { source: deleteIn } })

      const shouldRemoveFromGlobal =
        (deleteIn === 'community' && !topicPath) || (deleteIn === 'topic' && !!topicPath)

      queryClient.setQueryData(
        communityPostKeys.pinnedList(communityPath, topicPath),
        mutatePostsByPin({
          post,
          shouldRemove: shouldRemoveFromGlobal,
          modifier: pinnedTo => pinnedTo.filter(pin => pin !== deleteIn),
        }),
      )

      if (shouldRemoveFromGlobal) {
        const listKey = topicPath
          ? communityPostKeys.topicList(communityPath as string, topicPath as string)
          : communityPostKeys.list(communityPath as string)

        queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
          { queryKey: listKey },
          old => {
            if (!old || !('pages' in old)) return old

            const firstPage = old.pages[0]
            if (firstPage && !firstPage.items.some(p => p.id === post.id)) {
              return {
                ...old,
                pages: [
                  {
                    ...firstPage,
                    items: [
                      { ...post, pinnedTo: post.pinnedTo.filter(p => p !== deleteIn) },
                      ...firstPage.items,
                    ],
                  },
                  ...old.pages.slice(1),
                ],
              }
            }
            return old
          },
        )

        queryClient.invalidateQueries({ queryKey: listKey })
      }

      toast.success(t('community.actions.unpin_post.success'))
    } catch {
      toast.error(t('core.error.internal_error_message'))
    }
  }

  return (
    <div
      className={twMerge(
        'flex h-[30px] items-center justify-between bg-brand-primary px-5',
        className,
      )}
    >
      <div className="flex items-center justify-center">
        <PinIcon className="text-background-secondary" />
        <span className="ml-2 text-[15px] font-semibold text-background-secondary">
          {t('pinned.post_pinned_title')}
        </span>
      </div>
      <div>
        {isPostUnpinnable && (
          <Menu as="div" className="relative hover:text-brand-primary">
            <MenuButton className="text-sm font-normal text-background-secondary underline">
              {t('pinned.post_unpin_button')}
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                as="ul"
                anchor="bottom end"
                className="flex w-max max-w-sm min-w-[144px] flex-col rounded-[10px] bg-background-secondary px-5 py-3 shadow-menu"
              >
                {(['community', 'topic'] as PostPinnedToType[]).map(
                  source =>
                    isPinnedIn(post, source) && (
                      <MenuItem as="li" key={`unpin-target-${source}`}>
                        <button
                          className="flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary hover:text-text-primary/70 focus:text-text-primary/70"
                          onClick={() => handleUnpin(post, source)}
                        >
                          <PinIcon className="size-4 text-text-secondary" />
                          {t(`community.actions.unpin_post.${source}`)}
                        </button>
                      </MenuItem>
                    ),
                )}
              </MenuItems>
            </Transition>
          </Menu>
        )}
      </div>
    </div>
  )
}
